import Collection from '@arcgis/core/core/Collection';
import { eachAlways } from '@arcgis/core/core/promiseUtils';
import { previewType } from 'src/app/widgets/AppTypeTile';

export enum EWebmapNotCompatible {
  UnsupportedRenderers_FeatureLayer = "ilReq",
  UnsupportedRenderers_Renderer = "ilReq",
  PopupDisabled = "nearReq",
  ImageryCondition = "imgReq",
  AttachmentsCondition = "avReq",
  NotWebmapCompatible = "noWebmapReq",
  CatGalRequirement = "catGallReq",
  ThreeDRequirement = "threeDReq"
}

export enum EAppTypeIdentifier {
  ThreeDViewer = "/apps/instant/3dviewer/index.html",
  AttachmentViewer = "/apps/instant/attachmentviewer/index.html",
  CategoryGallery = "/apps/instant/filtergallery/index.html",
  Countdown = "/apps/instant/countdown/index.html",
  InteractiveLegend = "/apps/instant/interactivelegend/index.html",
  ImageryApp = "/apps/instant/imageryviewer/index.html",
  Media = "/apps/instant/media/index.html",
  Minimalist = "/apps/instant/minimalist/index.html",
  Nearby = "/apps/instant/nearby/index.html",
  Portfolio = "/apps/instant/portfolio/index.html",
  ZoneLookup = "/apps/instant/lookup/index.html",
}

export const NotCompat_to_AppType_Mapping = {
  [EWebmapNotCompatible.UnsupportedRenderers_FeatureLayer]: [EAppTypeIdentifier.InteractiveLegend],
  [EWebmapNotCompatible.UnsupportedRenderers_Renderer]: [EAppTypeIdentifier.InteractiveLegend],
  [EWebmapNotCompatible.PopupDisabled]: [EAppTypeIdentifier.Nearby, EAppTypeIdentifier.ZoneLookup],
  [EWebmapNotCompatible.ImageryCondition]: [EAppTypeIdentifier.ImageryApp],
  [EWebmapNotCompatible.AttachmentsCondition]: [EAppTypeIdentifier.AttachmentViewer],
  // [EWebmapNotCompatible.NotWebmapCompatible]               : [EAppTypeIdentifier.CategoryGallery, EAppTypeIdentifier.ThreeDViewer]
  [EWebmapNotCompatible.CatGalRequirement]: [EAppTypeIdentifier.CategoryGallery],
  [EWebmapNotCompatible.ThreeDRequirement]: [EAppTypeIdentifier.ThreeDViewer]
}



export async function performWebmapChecks(webmap: __esri.WebMap): Promise<EWebmapNotCompatible[]> {
  try {
    await webmap.loadAll();
  } catch (err) {
    console.error("webmap ::: Loading resources error:", err);
  }

  return [
    await _testUnsupportedRenderers(webmap),
    _testPopupDisabled(webmap),
    _testImageryCondition(webmap),
    await _testAttachmentsCondition(webmap),
    EWebmapNotCompatible.CatGalRequirement,
    EWebmapNotCompatible.ThreeDRequirement
  ].filter(val => val != null);
}

export function convertNotCompatToApps(notCompats: EWebmapNotCompatible[]): EAppTypeIdentifier[] {
  return notCompats.map(
    notCompat => NotCompat_to_AppType_Mapping[notCompat]
  ).reduce((acc, curr) => {
    return [...acc, ...curr];
  }, [])
}

/** For an appType, return all EWebmapNotCompatible types, for the specific notCompatsList, that are associated with it. */
export function getNotCompats_Specific(appType: EAppTypeIdentifier, notCompatsList: EWebmapNotCompatible[]): EWebmapNotCompatible[] {
  return notCompatsList.map((key: EWebmapNotCompatible) => {
    const appTypes: EAppTypeIdentifier[] = NotCompat_to_AppType_Mapping[key];
    return appTypes.indexOf(appType) !== -1 ? key : null;
  }).filter(val => val != null);
}

/** For an appType, return all EWebmapNotCompatible types that are associated with it. */
export function getNotCompats_General(appType: EAppTypeIdentifier): EWebmapNotCompatible[] {
  return Object.keys(NotCompat_to_AppType_Mapping).map((key: EWebmapNotCompatible) => {
    const appTypes: EAppTypeIdentifier[] = NotCompat_to_AppType_Mapping[key];
    return appTypes.indexOf(appType) !== -1 ? key : null;
  }).filter(val => val != null);
}

async function _testUnsupportedRenderers(webmap: __esri.WebMap): Promise<EWebmapNotCompatible | null> {
  let atLeastOneSupportedRenderer: boolean = false;
  let atLeastOneFeatureLayer: boolean = false;
  webmap?.allLayers?.map(layer => {
    const type = layer?.type;

    if (type === "group") {
      return;
    }

    if (type !== "feature") {
      return;
    }
    atLeastOneFeatureLayer = atLeastOneFeatureLayer || true;

    const featureLayer = layer as __esri.FeatureLayer;
    let isLayerRendererSupported: boolean = true;

    // PROPS SET BY ARCADE
    const field2 = featureLayer?.renderer?.get("field2");
    const field3 = featureLayer?.renderer?.get("field3");
    const fieldDelimiter = featureLayer?.renderer?.get("fieldDelimiter");
    if ((field2 || field3) && fieldDelimiter) {
      isLayerRendererSupported = false;
    }

    if (
      featureLayer?.renderer?.type === "unique-value" ||
      featureLayer?.renderer?.type === "class-breaks"
    ) {
      // CHECK VISUAL VARIABLES for color ramp, size ramp, opacity ramp
      const renderer = featureLayer.renderer as any;
      renderer?.visualVariables?.forEach(visualVariable => {
        if (
          visualVariable.type === "size" ||
          visualVariable.type === "color" ||
          visualVariable.type === "opacity"
        ) {
          const rendererAuthoringInfoType = renderer?.authoringInfo?.type;
          if (
            rendererAuthoringInfoType === "class-breaks-color" ||
            rendererAuthoringInfoType === "class-breaks-size" ||
            featureLayer?.renderer?.type === "unique-value"
          ) {
            return;
          }
          isLayerRendererSupported = false;
        }
      });
    }

    if (
      featureLayer?.renderer?.type === "heatmap" ||
      featureLayer?.renderer?.type === "dot-density"
    ) {
      isLayerRendererSupported = false;
    }

    // SINGLE SYMBOL(UNIQUE SYMBOL) - CLUSTERING ENABLED.
    // Drawing style is similar to Counts and Amounts (Size/Color) Classify Data Unchecked which is unsupported.
    const simpleRenderer = (featureLayer?.renderer?.type === "simple"
      ? featureLayer.renderer
      : null) as __esri.SimpleRenderer;
    const vvSizeArr = simpleRenderer?.visualVariables?.filter(
      visualVariable => visualVariable.type === "size"
    );
    if (
      (simpleRenderer && featureLayer?.featureReduction) ||
      (vvSizeArr?.length > 0 && featureLayer?.featureReduction)
    ) {
      isLayerRendererSupported = false;
    }

    // *** accumulate supportedRenderer info ***
    // (if isLayerRendererSupported is true, we know this layer is supported, and with the || it will turn atLeastOneSupportedRenderer to true)
    atLeastOneSupportedRenderer = atLeastOneSupportedRenderer || isLayerRendererSupported;
  });

  if (!atLeastOneFeatureLayer) {
    return EWebmapNotCompatible.UnsupportedRenderers_FeatureLayer;
  } else if (!atLeastOneSupportedRenderer) {
    return EWebmapNotCompatible.UnsupportedRenderers_Renderer;
  } else {
    return null;
  }
}

/**
 * If there does not exist at least one layer in the webmap has is type "imagery" or "imagery-tile", 
 *    return EWebmapNotCompatible.ImageryCondition, else return null (Compatible)
 */
function _testImageryCondition(webmap: __esri.WebMap): EWebmapNotCompatible | null {
  let atLeastOneImageryLayer: boolean = webmap?.allLayers?.map(layer => {
    return layer.type === "imagery" || layer.type === "imagery-tile";
  }).reduce((acc: boolean, curr: boolean) => { return acc || curr }, false);

  return !atLeastOneImageryLayer ? EWebmapNotCompatible.ImageryCondition : null;
}

function _testAttachmentsCondition(webmap: __esri.WebMap): Promise<EWebmapNotCompatible | null> {
  return new Promise((res, rej) => {
    let isPassing = false;
    const layerPromises = eachAlways(
      webmap?.allLayers?.map(layer => {
        return layer.load().then(() => {
          if (layer.type === "feature") {
            const featureLayer = layer as __esri.FeatureLayer;
            if (
              featureLayer.capabilities?.operations?.supportsQueryAttachments ||
              featureLayer.capabilities?.data?.supportsAttachment
            ) {
              isPassing = isPassing || true;
            }
          }
        });
      })
    );

    layerPromises.then(() => {
      if (!isPassing) {
        res(EWebmapNotCompatible.AttachmentsCondition);
      } else {
        res(null);
      }
    });
  });
}

/** 
 * If all layers in the webmap have popups disabled, 
 *    return EWebmapNotCompatible.PopupDisabled, else return null (Compatible) 
 */
function _testPopupDisabled(webmap: __esri.WebMap): EWebmapNotCompatible | null {
  const excludeTypes = new Collection(["tile", "base-tile", "imagery-tile", "vector-tile", "web-tile"]);

  let atLeastOnePopupEnabled: boolean = webmap?.allLayers?.filter((layer)=>{
    return !excludeTypes.includes(layer.type)
  }).map(layer => {
    return layer.get("popupEnabled") as boolean;
  }).reduce((acc: boolean, curr: boolean) => { return acc || curr }, false);

  return !atLeastOnePopupEnabled ? EWebmapNotCompatible.PopupDisabled : null;
}